import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { franchiseMapIndex, titleMapIndex } from "../../../core/constants";
import CoverUploadModal from "../../../shared/components/CoverUploadModal";
import ScreenReleaseSelector from "../../admin/components/ScreenReleaseSelector";

// Reading orders category "Cinematic Universes"
const CINEMATIC_UNIVERSES_TYPE = "6";

function ReadingOrderForm({
  handleSubmit,
  readingOrder,
  previewReadingOrder,
  setPreviewReadingOrder,
  loading,
  showPremiumOnly = false,
  showScreenReleases = false,
}) {
  const location = useLocation();
  const { isOpen, onToggle } = useDisclosure();
  const isPublishing = location.pathname.includes("publish");
  const [title, setTitle] = useState(readingOrder?.title);
  const order = readingOrder?.order || 1;
  const [type, setType] = useState(readingOrder?.type);
  const [description, setDescription] = useState(
    readingOrder?.description || ""
  );
  const [fromYear, setFromYear] = useState(readingOrder?.fromYear || "");
  const [toYear, setToYear] = useState(readingOrder?.toYear || "");
  const [isOngoing, setIsOngoing] = useState(!!readingOrder?.isOngoing);
  const [franchise, setFranchise] = useState(`${readingOrder?.franchise}`);
  const [published, setPublished] = useState(
    `${readingOrder?.published ? "1" : "0"}`
  );
  const [premiumOnly, setPremiumOnly] = useState(!!readingOrder?.premiumOnly);
  // Movies / show seasons linked to a Cinematic Universe order (admin only)
  const [screenReleases, setScreenReleases] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(readingOrder?.thumbnail);

  const onCropSubmitted = (file, url) => {
    setCoverImage(file);
    setPreviewUrl(url);
    setPreviewReadingOrder({
      ...previewReadingOrder,
      thumbnail: url,
    });
    onToggle();
  };

  const setYears = (from, to, isOngoing) => {
    // E.g: 2015
    if (from === to) {
      return from;
    }

    // E.g: 2001-Present, 2001-Ongoing
    if (from && !to) {
      return `${from}-${isOngoing ? "Ongoing" : "Present"}`;
    }

    // E.g: 2001-2005
    if (from && to) {
      return `${from}-${to}`;
    }

    return "";
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit({
      title,
      description,
      franchise,
      order,
      type,
      published,
      thumbnail: coverImage,
      fromYear,
      toYear,
      isOngoing: isOngoing ? "1" : "0",
      ...(showPremiumOnly && { premiumOnly: premiumOnly ? "1" : "0" }),
      ...(showScreenReleases && {
        screenReleaseIds: screenReleases.map((release) => release.id),
      }),
    });
  };

  return (
    <form onSubmit={onSubmit}>
      {isPublishing && (
        <Box bg="gray.800" p="3" borderRadius="lg" mb="3">
          <FormControl isRequired>
            <FormLabel>Status of publication</FormLabel>
            <RadioGroup
              value={published}
              onChange={(value) => setPublished(value)}
            >
              <HStack spacing="24px">
                <Radio value="0" tabIndex={6}>
                  Draft
                </Radio>
                <Radio value="1" tabIndex={6}>
                  Published
                </Radio>
              </HStack>
            </RadioGroup>
          </FormControl>
          <Text fontSize="sm" pt="2">
            If you believe that your reading order is ready to be shared with
            other users you may publish it. Publishing your order will
            automatically add it to your Reading.
          </Text>
        </Box>
      )}
      <VStack spacing={4}>
        <FormControl id="title" isRequired>
          <FormLabel>Name your reading order</FormLabel>
          <Input
            name="title"
            type="text"
            maxLength={50}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setPreviewReadingOrder({
                ...previewReadingOrder,
                title: e.target.value,
              });
            }}
            required
            tabIndex={1}
          />
        </FormControl>
        <FormControl id="description">
          <FormLabel>Description</FormLabel>
          <Textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setPreviewReadingOrder({
                ...previewReadingOrder,
                description: e.target.value,
              });
            }}
            tabIndex={2}
          />
        </FormControl>
        <Flex alignItems="center" justifyContent="space-between" w="100%">
          <Box>
            <FormControl id="fromYear" required>
              <FormLabel>From</FormLabel>
              <Input
                name="fromYear"
                type="text"
                placeholder="1961"
                value={fromYear}
                onChange={(e) => {
                  setFromYear(e.target.value);
                  setPreviewReadingOrder({
                    ...previewReadingOrder,
                    years: setYears(e.target.value, toYear, isOngoing),
                  });
                }}
                tabIndex={3}
              />
            </FormControl>
          </Box>
          <Box px="3">
            <FormControl id="toYear">
              <FormLabel>To</FormLabel>
              <Input
                name="toYear"
                type="text"
                value={toYear}
                onChange={(e) => {
                  setToYear(e.target.value);
                  setPreviewReadingOrder({
                    ...previewReadingOrder,
                    years: setYears(fromYear, e.target.value, isOngoing),
                  });
                }}
                placeholder="Present"
                tabIndex={4}
              />
            </FormControl>
          </Box>
          <Box>
            <FormControl id="ongoing">
              <FormLabel>&nbsp;</FormLabel>
              <Checkbox
                isChecked={isOngoing}
                onChange={() => {
                  setIsOngoing(!isOngoing);
                  setPreviewReadingOrder({
                    ...previewReadingOrder,
                    years: setYears(fromYear, toYear, !isOngoing),
                  });
                }}
                tabIndex={5}
              >
                Ongoing?
              </Checkbox>
            </FormControl>
          </Box>
        </Flex>
        <Flex w="100%">
          <Box w="50%">
            <FormControl isRequired>
              <FormLabel>Franchise</FormLabel>
              <Select
                value={franchise}
                onChange={(e) => {
                  setFranchise(e.target.value);
                  setPreviewReadingOrder({
                    ...previewReadingOrder,
                    franchise: e.target.value,
                  });
                }}
                tabIndex={6}
              >
                <option value="" tabIndex={5}>
                  Select a franchise
                </option>
                {Object.keys(franchiseMapIndex).map((franchise, index) => (
                  <option key={franchise} value={franchise}>
                    {franchiseMapIndex[franchise]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box w="50%" pl="3">
            <FormControl isRequired>
              <FormLabel>Category</FormLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                tabIndex={7}
              >
                <option value="">Select a category</option>
                {Object.keys(titleMapIndex).map((type, index) => (
                  <option key={type} value={type}>
                    {titleMapIndex[type]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Flex>
        {showScreenReleases && `${type}` === CINEMATIC_UNIVERSES_TYPE && (
          <FormControl id="screenReleases">
            <ScreenReleaseSelector
              readingOrderId={readingOrder?.id}
              value={screenReleases}
              onChange={setScreenReleases}
            />
          </FormControl>
        )}
        {showPremiumOnly && (
          <FormControl id="premiumOnly">
            <Checkbox
              isChecked={premiumOnly}
              onChange={(event) => setPremiumOnly(event.target.checked)}
              tabIndex={8}
            >
              Premium only
            </Checkbox>
            <Text fontSize="sm" color="gray.400" mt="1">
              Only Premium members can open this reading order.
            </Text>
          </FormControl>
        )}
        <Flex alignItems="center" justifyContent="space-between" w="100%">
          <Box>
            <Button as="label" htmlFor="coverImage" onClick={onToggle}>
              {previewUrl ? "Edit cover image" : "Upload a cover image"}
            </Button>
            {/* <FormControl isRequired>
              <FormLabel>Cover Image</FormLabel>
              <label htmlFor="cover">
                <Button
                  disabled={!!previewUrl}
                  as="label"
                  htmlFor="coverImage"
                  onClick={onToggle}
                >
                  {previewUrl ? "Your image" : "Upload a cover image"}
                </Button>
              </label>
              <input
                type="file"
                width="100%"
                accept="image/jpeg"
                style={{ display: "none" }}
                id="coverImage"
                onChange={handleFileInputChange}
                tabIndex={8}
              />
            </FormControl> */}
            <CoverUploadModal
              onToggle={onToggle}
              isOpen={isOpen}
              onSubmit={onCropSubmitted}
              title="Upload a reading order cover image"
              aspectRatio={1000 / 640}
              croppedCanvasConfig={{
                width: 1000,
                height: 640,
                minWidth: 1000,
                minHeight: 640,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: "high",
              }}
            />
          </Box>
        </Flex>

        <Button
          type="submit"
          colorScheme="blue"
          tabIndex={9}
          textColor="white"
          isLoading={loading}
          loadingText="Saving reading order"
          spinnerPlacement="start"
        >
          {readingOrder ? "Update" : "Create"} reading order
        </Button>
      </VStack>
    </form>
  );
}

export default ReadingOrderForm;
